<template>
  <div
    class="relative my-11.25 w-full text-black md:my-15 xl:my-17.5 2xl:my-20"
    ref="dropdownRef"
  >
    <button
      type="button"
      @click="toggleDropdown"
      class="xl:leading-[120% flex w-full cursor-pointer items-center justify-between border border-black bg-transparent p-2.5 focus:outline-none md:text-[17px] md:leading-[123%] xl:text-xl 2xl:p-3.75 2xl:text-[23px]/[28px]"
      :aria-expanded="isOpen"
    >
      <span class="truncate">
        {{
          typeof modelValue?.name === 'string'
            ? modelValue?.name
            : modelValue?.name[locale]
        }}
      </span>

      <div
        class="flex h-6.5 w-6.5 shrink-0 items-center justify-center rounded-full bg-black"
      >
        <ArrowIcon
          class="h-3.5 w-3.5 transition-transform duration-300 ease-in-out"
          :class="[arrowColorClass, isOpen ? 'rotate-270' : 'rotate-90']"
          :style="arrowStyle"
        />
      </div>
    </button>

    <Transition
      enter-active-class="transition-all duration-400 ease-out"
      enter-from-class="grid-rows-[0fr] opacity-0"
      enter-to-class="grid-rows-[1fr] opacity-100"
      leave-active-class="transition-all duration-300 ease-in"
      leave-from-class="grid-rows-[1fr] opacity-100"
      leave-to-class="grid-rows-[0fr] opacity-0"
    >
      <div v-if="isOpen" class="absolute top-full left-0 z-50 grid w-full">
        <div class="min-h-0 overflow-hidden">
          <div
            :class="[
              'max-h-80.5 w-full overflow-auto border border-t-0 border-black',
              bgColor,
            ]"
          >
            <ul class="flex flex-col">
              <li
                v-for="(option, index) in options"
                :key="index"
                class="border-b border-black last:border-b-0"
              >
                <button
                  type="button"
                  @click="selectOption(option)"
                  :class="[
                    'w-full cursor-pointer p-2.5 text-left text-base transition-colors hover:bg-black hover:text-white focus:bg-black focus:outline-none md:text-[17px] md:leading-[123%] 2xl:p-3.75 2xl:text-[23px]/[28px]',
                    {
                      [bgColor]: modelValue?.slug !== option.slug,
                      'bg-black text-white': modelValue?.slug === option.slug,
                    },
                  ]"
                >
                  {{
                    typeof option.name === 'string'
                      ? option.name
                      : option.name[locale]
                  }}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import type { LocalizedString } from '~/types'

interface SelectOption {
  name: string | LocalizedString
  slug: string
}

interface Props {
  modelValue: SelectOption | null
  options: SelectOption[]
  placeholder?: string
  bgColor?: string
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Vyberte...',
  bgColor: 'bg-white',
})

const { locale } = useI18n()
const emit = defineEmits(['update:modelValue'])

const isOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)

const arrowStyle = computed(() => {
  const match = props.bgColor?.match(/\[(.*?)\]/)
  return match ? { color: match[1] } : {}
})

const arrowColorClass = computed(() => {
  if (props.bgColor?.includes('[')) return ''
  return props.bgColor ? props.bgColor.replace(/^bg-/, 'text-') : 'text-white'
})

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}

const selectOption = (option: SelectOption) => {
  emit('update:modelValue', option)
  isOpen.value = false
}

const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isOpen.value = false
  }
}

onMounted(() => {
  if (process.client) {
    document.addEventListener('click', handleClickOutside)
  }
})

onUnmounted(() => {
  if (process.client) {
    document.removeEventListener('click', handleClickOutside)
  }
})
</script>
